import React, { useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { Box, Typography } from '@mui/material';
import TableComponent, { HeadCell } from '../../component/table/table.component';
import CardView from '../../component/card/card-view';
import { PILL_FILTER_CONTROL_WIDTH_SX } from '../../component/pill-filter-control-sx';
import PrefixLabeledPillSelect from '../../component/prefix-labeled-pill-select.component';
import i18n from '../../i18n';
import {
    dateFormat,
    formatEventTypeText,
    formatRiskMinutes,
    formatRiskMoney,
    formatRiskStatusText,
    formatRiskTypeText,
} from '../../constants/tool-order';
import { RiskEvent, RiskOrder } from '../../models';
import riskOrderListState from './risk-order-list-state';
import { getRiskTagsForOrder, RiskOrderGroup } from './risk-order-mode';

/** 表格模式：异常数值列高亮（与云端列表一致） */
const riskHighlightColor = 'var(--func-error)';

/**
 * 判断订单聚合项是否包含指定规则风险类型。
 * @param order - 即时风险订单或订单模式聚合订单
 * @param riskType - 规则风险类型编号
 * @returns 订单包含该风险类型时返回 true
 */
export function isRiskTypeHighlighted(order: RiskOrderGroup, riskType: number): boolean {
    return getRiskTagsForOrder(order).some((tag) => tag.type === 'risk' && tag.value === riskType);
}

/**
 * 即时风险订单列表 - 表格模式（布局与云端风险订单列表 Card + 表头行一致）
 */
const RiskOrderListTable = observer(() => {
    /** 列表类型下拉：异常订单 / 异常事件（药丸样式） */
    const tableListTypeOptions = useMemo(
        () => [
            {
                value: 'order',
                label: i18n.t('intelligent_risk_control_page_text_abnormal_orders'),
            },
            {
                value: 'event',
                label: i18n.t('intelligent_risk_control_page_text_abnormal_events'),
            },
        ],
        []
    );

    const formatDateTime = (val?: string) => {
        if (!val) {
            return '-';
        }
        return dateFormat(val, 'HH:mm DD/MM/YYYY');
    };

    /** 异常订单表头（与云端风险订单列表字段对齐，无服务端排序） */
    const orderHeadCells: HeadCell[] = useMemo(
        () => [
            {
                id: 'order_head_id',
                label: i18n.t('business_analysis_page_order_account'),
            },
            {
                id: 'table_name',
                label: i18n.t('business_analysis_page_table_name'),
            },
            {
                id: 'people_count',
                label: i18n.t('immediate_number_people'),
            },
            {
                id: 'dish_count',
                label: i18n.t('intelligent_risk_control_page_column_dish_count'),
            },
            {
                id: 'operator_name',
                label: i18n.t('intelligent_risk_control_page_column_server'),
            },
            {
                id: 'open_time',
                label: i18n.t('immediate_table_opening_time'),
                renderFunction: (row) => formatDateTime((row as RiskOrder).open_time),
            },
            {
                id: 'close_time',
                label: i18n.t('immediate_statement_time'),
                renderFunction: (row) => formatDateTime((row as RiskOrder).close_time),
            },
            {
                id: 'amount',
                label: i18n.t('intelligent_risk_control_page_column_amount_original_actual'),
                renderFunction: (row) => {
                    const r = row as RiskOrder;
                    return `${formatRiskMoney(r.origin_amount)} / ${formatRiskMoney(r.actual_amount)}`;
                },
            },
            {
                id: 'discount_amount',
                label: i18n.t('intelligent_risk_control_page_column_discount_amount'),
                renderFunction: (row) => {
                    const r = row as RiskOrderGroup;
                    const value = formatRiskMoney(r.discount_amount);
                    const highlight = isRiskTypeHighlighted(r, 1) && r.discount_amount ? riskHighlightColor : undefined;
                    return <span style={{ color: highlight }}>{value}</span>;
                },
            },
            {
                id: 'refund_dish_count',
                label: i18n.t('intelligent_risk_control_page_column_refund_count'),
                renderFunction: (row) => {
                    const r = row as RiskOrderGroup;
                    const highlight = isRiskTypeHighlighted(r, 2) ? riskHighlightColor : undefined;
                    return <span style={{ color: highlight }}>{r.refund_dish_count}</span>;
                },
            },
            {
                id: 'refund_amount',
                label: i18n.t('intelligent_risk_control_page_column_refund_amount'),
                renderFunction: (row) => {
                    const r = row as RiskOrderGroup;
                    const value = formatRiskMoney(r.refund_amount);
                    const highlight = isRiskTypeHighlighted(r, 3) && r.refund_amount ? riskHighlightColor : undefined;
                    return <span style={{ color: highlight }}>{value}</span>;
                },
            },
            {
                id: 'avg_dish_per_person',
                label: i18n.t('intelligent_risk_control_page_column_avg_dish_count'),
                renderFunction: (row) => {
                    const r = row as RiskOrderGroup;
                    const highlight = isRiskTypeHighlighted(r, 4) ? riskHighlightColor : undefined;
                    return <span style={{ color: highlight }}>{r.avg_dish_per_person}</span>;
                },
            },
            {
                id: 'dining_duration_min',
                label: i18n.t('intelligent_risk_control_page_column_duration_minutes'),
                renderFunction: (row) => {
                    const r = row as RiskOrderGroup;
                    const value = formatRiskMinutes(r.dining_duration_min);
                    const highlight = isRiskTypeHighlighted(r, 5) ? riskHighlightColor : undefined;
                    return <span style={{ color: highlight }}>{value}</span>;
                },
            },
            {
                id: 'risk_tag',
                label: i18n.t('intelligent_risk_control_page_filter_risk_tag'),
                renderFunction: (row) => (
                    <span style={{ color: riskHighlightColor }}>
                        {getRiskTagsForOrder(row as RiskOrderGroup)
                            .map((tag) =>
                                i18n.t(tag.type === 'risk' ? formatRiskTypeText(tag.value) : formatEventTypeText(tag.value))
                            )
                            .join('、') || '-'}
                    </span>
                ),
            },
            {
                id: 'risk_status',
                label: i18n.t('restaurant_info_page_status'),
                renderFunction: (row) => i18n.t(formatRiskStatusText((row as RiskOrder).orderStatus ?? (row as RiskOrder).risk_status)),
            },
        ],
        []
    );

    /** 异常事件表头（与导出 Excel 列一致） */
    const eventHeadCells: HeadCell[] = useMemo(
        () => [
            {
                id: 'eventType',
                label: i18n.t('intelligent_risk_control_page_filter_risk_tag'),
                renderFunction: (row) => (
                    <span style={{ color: riskHighlightColor }}>
                        {i18n.t(formatEventTypeText((row as RiskEvent).eventType))}
                    </span>
                ),
            },
            {
                id: 'order_head_ids',
                label: i18n.t('business_analysis_page_order_account'),
                renderFunction: (row) => {
                    const event = row as RiskEvent;
                    return event.orders?.map((o) => o.order_head_id).join(', ') || '-';
                },
            },
            {
                id: 'createdAt',
                label: i18n.t('restaurant_extensions_page_text_create_time'),
                renderFunction: (row) => formatDateTime((row as RiskEvent).createdAt),
            },
            {
                id: 'event_status',
                label: i18n.t('restaurant_info_page_status'),
                renderFunction: (row) =>
                    i18n.t(formatRiskStatusText((row as RiskEvent).orders?.[0]?.event_status ?? 0)),
            },
        ],
        []
    );

    const isOrderTable = riskOrderListState.tableListType === 'order';
    const tableList = riskOrderListState.tableListData;

    return (
        <CardView
            title=""
            className="mt16"
            contentRender={(
                <>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 2,
                            rowGap: 2,
                            flexWrap: 'wrap',
                            mb: 2,
                        }}
                    >
                        <Typography variant="h3" sx={{ color: 'var(--func-sp1)', flexShrink: 0 }}>
                            {i18n.t('intelligent_risk_control_page_text_risk_order_list')}
                        </Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                rowGap: 2,
                                flexWrap: 'wrap',
                                justifyContent: 'flex-end',
                                flex: '1 1 auto',
                                minWidth: 0,
                            }}
                        >
                            <PrefixLabeledPillSelect
                                options={tableListTypeOptions}
                                value={riskOrderListState.tableListType}
                                onChange={(value) => {
                                    riskOrderListState.setTableListType(value as 'order' | 'event');
                                }}
                                sx={PILL_FILTER_CONTROL_WIDTH_SX}
                            />
                        </Box>
                    </Box>
                    <Box>
                        <TableComponent
                            isPagination
                            isUseCustomPagination
                            allCount={tableList.length}
                            headCellList={isOrderTable ? orderHeadCells : eventHeadCells}
                            list={tableList}
                            showChecked={0}
                            isTableLoading={riskOrderListState.loading}
                            changePageCallback={(params) => {
                                riskOrderListState.changeTablePage(params.page, params.curRowsPerPage);
                            }}
                            currentPage={riskOrderListState.tablePage}
                            onRowClick={(row) => {
                                if (isOrderTable) {
                                    riskOrderListState.openOrderDialog(row as RiskOrder);
                                } else {
                                    riskOrderListState.openEventDialog(row as RiskEvent);
                                }
                            }}
                        />
                    </Box>
                </>
            )}
        />
    );
});

export default RiskOrderListTable;
